
/**
 * Custom Web Component for seamless scrolling
 * Supports:
 * - Horizontal / Vertical
 * - Infinite seamless loop
 * - RTL
 * - Lazy init with IntersectionObserver
 * - Resize re-init
 */

if (!customElements.get("scroll-seamless")) {
  customElements.define(
    "scroll-seamless",
    class ScrollSeamless extends HTMLElement {
      connectedCallback() {
        this.listContainer = this.querySelector(".scroll-list");

        if (!this.listContainer) return;

        this.ifVertical = this.hasAttribute("data-vertical");

        this.scrollDirection =
          this.getAttribute("data-direction") ||
          (this.ifVertical ? "up" : "left");

        this.speed =
          parseInt(this.getAttribute("data-speed"), 10) || 40;

        this.currentTrans = 0;
        this.lastWindowWidth = window.innerWidth;

        this.observeInView();

        // Design mode resize support
        if (window.Shopify?.designMode || window.debug) {
          this.debounceWindowSizeChangeHandler =
            window.webvista?.debounce
              ? window.webvista.debounce(
                  this.onWindowSizeChange.bind(this),
                  300
                )
              : this.onWindowSizeChange.bind(this);

          window.addEventListener(
            "resize",
            this.debounceWindowSizeChangeHandler
          );
        }
      }

      disconnectedCallback() {
        if (this.debounceWindowSizeChangeHandler) {
          window.removeEventListener(
            "resize",
            this.debounceWindowSizeChangeHandler
          );
        }
      }

      /**
       * Observe viewport entry
       */
      observeInView() {
        const observer = new IntersectionObserver(
          (entries, observer) => {
            if (entries[0].isIntersecting) {
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  this.init();
                });
              });

              observer.disconnect();
            }
          },
          {
            root: null,
            rootMargin: "100px 0px 100px 0px",
          }
        );

        observer.observe(this);
      }

      /**
       * Main init
       */
      init() {
        // Wait until element has size
        if (
          (this.ifVertical && this.clientHeight <= 0) ||
          (!this.ifVertical && this.clientWidth <= 0)
        ) {
          requestAnimationFrame(() => this.init());
          return;
        }

        this.getAllItems();

        if (this.items.length < 1) return;

        this.getOriginalTotalSize();

        if (this.originalSize <= 0) return;

        // Remove old clones
        this.querySelectorAll(".item-clone").forEach((item) =>
          item.remove()
        );

        // Clone items
        this.cloneItems();

        this.getAllItems();

        this.getGap();

        const transformDistance =
          this.originalSize + this.gap;

        // Set animation vars
        if (this.ifVertical) {
          if (this.scrollDirection === "up") {
            this.style.setProperty("--from", `0px`);
            this.style.setProperty(
              "--end",
              `-${transformDistance}px`
            );
          } else {
            this.style.setProperty(
              "--from",
              `${-transformDistance}px`
            );

            this.style.setProperty("--end", `0px`);
          }
        } else {
          const rtl =
            window.webvista?.isRTL?.() || false;

          if (
            this.scrollDirection === "left" ||
            (rtl && this.scrollDirection !== "right")
          ) {
            this.style.setProperty("--from", `0px`);

            this.style.setProperty(
              "--end",
              `-${transformDistance}px`
            );
          } else {
            this.style.setProperty(
              "--from",
              `-${transformDistance}px`
            );

            this.style.setProperty("--end", `0px`);
          }
        }

        this.style.setProperty(
          "--scroll-speed",
          `${transformDistance / this.speed}s`
        );

        this.setAttribute("data-init", "true");
      }

      /**
       * Resize re-init
       */
      onWindowSizeChange() {
        const currentWidth = window.innerWidth;

        if (currentWidth === this.lastWindowWidth) return;

        this.lastWindowWidth = currentWidth;

        if (!this.hasAttribute("data-init")) return;

        this.removeAttribute("data-init");

        requestAnimationFrame(() => {
          this.querySelectorAll(".item-clone").forEach((item) =>
            item.remove()
          );

          this.init();
        });
      }

      /**
       * Clone items
       */
      cloneItems() {
        const clientSize = this.ifVertical
          ? this.clientHeight
          : this.clientWidth;

        const times = Math.max(
          1,
          Math.ceil(clientSize / this.originalSize) + 1
        );

        const fragment =
          document.createDocumentFragment();

        for (let i = 0; i < times; i++) {
          this.items.forEach((item) => {
            const cloneItem = item.cloneNode(true);

            cloneItem.classList.add("item-clone");

            cloneItem.removeAttribute(
              "data-shopify-editor-block"
            );

            cloneItem.setAttribute(
              "aria-hidden",
              "true"
            );

            fragment.appendChild(cloneItem);
          });
        }

        this.listContainer.appendChild(fragment);

        // Re-init lazy images
        window.webvista?.initLazyImages?.();
      }

      /**
       * Get all items
       */
      getAllItems() {
        this.items = Array.from(
          this.querySelectorAll(".scroll-item")
        );
      }

      /**
       * Get original size
       */
      getOriginalTotalSize() {
        if (this.items.length < 1) {
          this.originalSize = 0;
          return;
        }

        if (this.ifVertical) {
          const firstItem = this.items[0];

          const lastItem =
            this.items[this.items.length - 1];

          this.originalSize =
            lastItem.offsetTop -
            firstItem.offsetTop +
            lastItem.offsetHeight;
        } else {
          const firstItem = this.items[0];

          const lastItem =
            this.items[this.items.length - 1];

          const rtl =
            window.webvista?.isRTL?.() || false;

          if (rtl) {
            this.originalSize =
              firstItem.offsetLeft -
              lastItem.offsetLeft +
              lastItem.offsetWidth;
          } else {
            this.originalSize =
              lastItem.offsetLeft -
              firstItem.offsetLeft +
              lastItem.offsetWidth;
          }
        }
      }

      /**
       * Get item gap
       */
      getGap() {
        if (this.items.length < 2) {
          this.gap = 0;
          return;
        }

        if (this.ifVertical) {
          this.gap =
            this.items[1].offsetTop -
            this.items[0].offsetTop -
            this.items[0].offsetHeight;
        } else {
          const rtl =
            window.webvista?.isRTL?.() || false;

          if (rtl) {
            this.gap =
              this.items[0].offsetLeft -
              this.items[1].offsetLeft -
              this.items[0].offsetWidth;
          } else {
            this.gap =
              this.items[1].offsetLeft -
              this.items[0].offsetLeft -
              this.items[0].offsetWidth;
          }
        }
      }

      /**
       * Reveal block in editor
       */
      moveItemVisible(item) {
        if (!this.items.includes(item)) return;

        const containerRect =
          this.getBoundingClientRect();

        const itemRect =
          item.getBoundingClientRect();

        let offset = 0;

        if (this.ifVertical) {
          if (itemRect.bottom > containerRect.bottom) {
            offset =
              containerRect.bottom - itemRect.bottom;
          } else if (
            itemRect.top < containerRect.top
          ) {
            offset =
              containerRect.top - itemRect.top;
          } else {
            return;
          }
        } else {
          if (itemRect.right > containerRect.right) {
            offset =
              containerRect.right - itemRect.right;
          } else if (
            itemRect.left < containerRect.left
          ) {
            offset =
              containerRect.left - itemRect.left;
          } else {
            return;
          }
        }

        this.currentTrans += offset;

        this.listContainer.style.transform =
          this.ifVertical
            ? `translateY(${this.currentTrans}px)`
            : `translateX(${this.currentTrans}px)`;
      }
    }
  );
}

